import { useQuery } from "@tanstack/react-query";
import api, { inr, num } from "@/lib/api";
import { PageHeader, Panel, Loading, StatCard, CHART_COLORS } from "@/components/kit";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
  PieChart, Pie, Legend,
} from "recharts";
import { Banknote, Percent, Wallet, Receipt } from "lucide-react";

export default function Finance() {
  const { data, isLoading } = useQuery({
    queryKey: ["dash-finance"],
    queryFn: async () => (await api.get("/dashboard/finance")).data,
  });
  if (isLoading || !data) return <Loading label="Loading finance dashboard…" />;
  const k = data.kpis;

  return (
    <div>
      <PageHeader title="Finance Dashboard" subtitle="Settlement disbursals, TDS, credit-note modes and ERP sync status" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testid="fin-kpi-gross" label="Gross Payable" value={inr(k.gross)} icon={Wallet} tone="navy" />
        <StatCard testid="fin-kpi-tds" label="TDS Deducted" value={inr(k.tds)} icon={Percent} tone="crimson" />
        <StatCard testid="fin-kpi-net" label="Net Settlement" value={inr(k.net)} icon={Banknote} tone="emerald" />
        <StatCard testid="fin-kpi-count" label="Settlement Records" value={num(k.count)} icon={Receipt} tone="gold" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Panel testid="panel-by-status" title="Net by Status" className="lg:col-span-2">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.by_status} margin={{ left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="status" tick={{ fontSize: 12, fill: "#334155" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => `${Math.round(v / 100000)}L`} />
              <Tooltip formatter={(v) => inr(v)} />
              <Bar dataKey="amount" radius={[8, 8, 0, 0]} barSize={70}>
                {data.by_status.map((e, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel testid="panel-by-mode" title="Payout Mode">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={data.by_mode} dataKey="amount" nameKey="mode" innerRadius={50} outerRadius={90} paddingAngle={3}>
                {data.by_mode.map((e, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => inr(v)} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <Panel testid="panel-erp-sync" title="ERP / Credit-Note Sync Status" subtitle="Integration-ready layer (no SAP posting in Phase 1)" className="mt-6">
        <div className="grid grid-cols-3 gap-4">
          {data.erp_sync.map((e) => (
            <div key={e.status} className="rounded-lg border border-slate-200 p-4 text-center" data-testid={`erp-${e.status}`}>
              <div className="text-3xl font-heading font-bold text-[#0F2C59]">{e.count}</div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mt-1">{e.status.replace("_", " ")}</div>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
